import React, { useMemo } from "react";
import "./Sparkline.css";

// Hand-rolled SVG bar chart for the daily-packs series.
// Expects [{ date: "YYYY-MM-DD", count: number }, ...].
// Fills missing days with 0 to keep the x-axis continuous.

function fillDays(series, days) {
    const map = new Map(series.map((p) => [p.date, p.count]));
    const out = [];
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setUTCDate(d.getUTCDate() - i);
        const key = d.toISOString().slice(0, 10);
        out.push({ date: key, count: map.get(key) ?? 0 });
    }
    return out;
}

function fmtDate(s) {
    const d = new Date(s + "T00:00:00Z");
    return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
    });
}

function Sparkline({
    series = [],
    days = 30,
    height = 80,
    label,
    accent = "var(--color-accent)",
}) {
    const points = useMemo(() => fillDays(series, days), [series, days]);
    const max = Math.max(1, ...points.map((p) => p.count));
    const total = points.reduce((s, p) => s + p.count, 0);
    const peak = points.reduce(
        (best, p) => (p.count > best.count ? p : best),
        { count: 0, date: "" }
    );

    const W = 600;
    const H = height;
    const PAD_X = 4;
    const PAD_Y = 8;
    const innerW = W - PAD_X * 2;
    const innerH = H - PAD_Y * 2;
    const slot = innerW / points.length;
    const barW = Math.max(2, slot - 2);

    return (
        <div className="sparkline">
            {label && (
                <div className="sparkline__head">
                    <span className="uppercase-label">{label}</span>
                    <span className="sparkline__total mono-num">
                        {total} <span className="sparkline__unit">total</span>
                    </span>
                </div>
            )}
            <svg
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                className="sparkline__svg"
                role="img"
                aria-label={`${total} packs over ${days} days`}
            >
                {/* Baseline */}
                <line
                    x1={PAD_X}
                    x2={W - PAD_X}
                    y1={H - PAD_Y}
                    y2={H - PAD_Y}
                    className="sparkline__baseline"
                />
                {points.map((p, i) => {
                    const h = (p.count / max) * innerH;
                    const x = PAD_X + i * slot + (slot - barW) / 2;
                    const y = H - PAD_Y - h;
                    const isPeak = peak.count > 0 && p.date === peak.date;
                    return (
                        <rect
                            key={p.date}
                            x={x}
                            y={y}
                            width={barW}
                            height={Math.max(h, p.count > 0 ? 1 : 0)}
                            rx={1}
                            className="sparkline__bar"
                            style={isPeak ? { fill: accent } : undefined}
                        >
                            <title>{`${fmtDate(p.date)}: ${p.count}`}</title>
                        </rect>
                    );
                })}
            </svg>
            <div className="sparkline__axis">
                <span className="mono">{fmtDate(points[0].date)}</span>
                <span className="mono">{fmtDate(points[points.length - 1].date)}</span>
            </div>
        </div>
    );
}

export default Sparkline;
